import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { api, apiError } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";

export default function DoctorProfile() {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ bio: "", photo_url: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/doctor/me").then((r) => {
      setMe(r.data);
      setForm({ bio: r.data.bio || "", photo_url: r.data.photo_url || "", phone: r.data.phone || "" });
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.patch("/doctor/me", form);
      setMe(r.data);
      toast.success("Profile saved");
    } catch (e) { toast.error(apiError(e, "Save failed")); } finally { setSaving(false); }
  };

  if (!me) return <div className="text-sm text-mb-fg2">Loading…</div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="doctor-profile">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ profile</div>
        <h1 className="mt-2 text-3xl font-light tracking-tight">Your profile</h1>
      </div>

      <div className="rounded-xl border border-mb-border bg-mb-surface p-5 grid grid-cols-2 gap-4" data-testid="doctor-profile-readonly">
        <RO label="Email" value={me.email} />
        <RO label="Hospital" value={me.hospital?.name || "—"} />
        <RO label="Specialty" value={me.specialty || "—"} />
        <RO label="Fee" value={formatCurrency(me.consultation_fee)} />
        <div className="col-span-2 text-[10px] uppercase tracking-[0.2em] text-zinc-600">
          Specialty, fee and schedule are managed by your hospital admin.
        </div>
      </div>

      <form onSubmit={submit} className="rounded-xl border border-mb-border bg-mb-surface p-5 grid sm:grid-cols-2 gap-4">
        <Field label="Phone" testid="doc-profile-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <Field label="Photo URL" testid="doc-profile-photo" value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} />
        <div className="sm:col-span-2 space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Bio</Label>
          <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={4}
            data-testid="doc-profile-bio"
            className="bg-mb-surface2 border-mb-border text-white rounded-lg focus-visible:ring-teal-500" />
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <Button type="submit" disabled={saving} data-testid="doc-profile-save"
            className="bg-teal-500 hover:bg-teal-400 text-zinc-950 rounded-lg h-10 px-5 font-medium">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="bg-mb-surface2 border-mb-border text-white h-11 rounded-lg focus-visible:ring-teal-500" />
    </div>
  );
}
function RO({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">{label}</div>
      <div className="text-sm text-white truncate">{value}</div>
    </div>
  );
}
